import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';

/**
 * One-time, post-transaction "save this person for next time?" prompt.
 *
 * Shared by Send Money (beneficiary) and Request Money (payer) so both flows
 * use the exact same UX/route. The prompt is only meant to be opened when the
 * counterparty is NOT already a saved contact (callers perform a normalized
 * dedup check before opening it).
 *
 * Persistence is delegated to the caller via `onSave` so each flow can do the
 * correct thing (create a unified beneficiary, or keep an already-created
 * one-time account). The component owns the loading/error UX around it.
 */
export interface SaveContactPromptProps {
  isOpen: boolean;
  /** Name of the person being saved (shown in the message). */
  contactName?: string;
  /** What we call the saved entity in this flow. */
  roleLabel?: 'beneficiary' | 'payer';
  /** Sentence describing what just happened, e.g. "Payment sent" / "Request sent". */
  outcomeText?: string;
  /** Persist the contact. Throwing here surfaces an inline error and keeps the prompt open. */
  onSave: () => Promise<void>;
  /** Called when the user declines (Not now) or dismisses the prompt. */
  onDecline?: () => void;
  /** Always called to hide the prompt (after save success, decline or dismiss). */
  onClose: () => void;
}

export function SaveContactPrompt({
  isOpen,
  contactName,
  roleLabel = 'beneficiary',
  outcomeText,
  onSave,
  onDecline,
  onClose,
}: SaveContactPromptProps) {
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const title = roleLabel === 'payer' ? 'Save Payer?' : 'Save Beneficiary?';
  const actionWord = roleLabel === 'payer' ? 'requests' : 'payments';
  const lead = outcomeText || (roleLabel === 'payer' ? 'Request sent' : 'Payment successful');
  const who = contactName?.trim() ? contactName.trim() : 'this person';

  const handleClose = () => {
    if (isSaving) return;
    setError(null);
    onClose();
  };

  const handleDecline = () => {
    if (isSaving) return;
    setError(null);
    onDecline?.();
    onClose();
  };

  const handleSave = async () => {
    setError(null);
    setIsSaving(true);
    try {
      await onSave();
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Could not save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent className="max-w-sm mx-auto" aria-describedby="save-contact-desc">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Montserrat, sans-serif' }}>{title}</DialogTitle>
          <DialogDescription id="save-contact-desc">
            Save for future quick access
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <p style={{ fontFamily: 'Montserrat, sans-serif' }}>
            {lead} to <span style={{ fontWeight: 600 }}>{who}</span>. Would you like to save them for quick {actionWord} next time?
          </p>

          {error && (
            <p
              role="alert"
              style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '13px', color: '#dc2626', margin: 0 }}
            >
              {error}
            </p>
          )}

          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={handleDecline}
              disabled={isSaving}
            >
              Not now
            </Button>
            <Button
              className="flex-1 bg-gradient-to-r from-[#86BE41] to-[#2D8CCA] text-white"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? 'Saving…' : (roleLabel === 'payer' ? 'Save Payer' : 'Save Beneficiary')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default SaveContactPrompt;
